import { unwrapResult } from "@reduxjs/toolkit";
import { nanoid } from "nanoid";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import PartsSelector, { PartSelection } from "components/PartsSelector";
import {
  ArrowBack as ArrowBackIcon,
  CropFree as CropFreeIcon,
  ErrorRounded as ErrorRoundedIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Autocomplete,
  CircularProgress,
  Dialog,
  FormHelperText,
  Grid,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { scanditLicense } from "env";
import { addressToPostalLocation, addressToString, isAbortError, resizeUploadFile } from "helpers";
import { CustomerType, FileType, JobCategoryType, Maybe } from "operations/schema/schema";
import { useAppDispatch, useAppSelector } from "store";
import {
  createJob,
  getAlertNotes,
  getCreateJobOptions,
  getCustomer,
  getCustomers,
  getEquipment,
  getEquipmentContractStatus,
  handleClose,
  selectCreateJobForm,
  selectCreateJobOpen,
  selectCreateJobOptions,
  setEquipmentMandatory,
  setValue,
  validateForm,
} from "store/slices/dialogs/createJob.store";
import { getJobs } from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { selectEngineerSettings } from "store/slices/user.store";
import { useDebounce } from "hooks/useDebounce";
import { AsolviSwitch } from "components/AsolviSwitch";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { BarcodeScannerDialog } from "components/barcodeScan";
import { EditPhoto } from "components/editPhoto/EditPhoto";
import { FilePreview } from "components/FilePreview";
import FullScreenDialog from "components/FullScreenDialog";
import PrimaryButton from "components/PrimaryButton";
import SecondaryButton from "components/SecondaryButton";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import { SymptomAutocomplete } from "components/SymptomAutocomplete";
import { getPartsCustomer } from "store/slices/cache.store";
import { CreateJobAlertNotesDialog } from "./CreateJobAlertNotesDialog";
import { asyncMutations } from "store/slices/dialogs/createSalesOrder.mutations";

const StyledDetails = styled("div")(() => ({
  marginTop: 5,
  marginLeft: 14,
}));

interface CreateJobDialogProps {
  customer?: Maybe<CustomerType>;
}

export const CreateJobDialog: FC<CreateJobDialogProps> = (props) => {
  const { customer: propsCustomer } = props;
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectCreateJobOpen);
  const engineerSettings = useAppSelector(selectEngineerSettings);
  const {
    initLoading,
    createJobLoading,
    isValid,
    isDirty,
    alertNotes,
    categories,
    customers,
    customersLoading,
    equipments,
    equipmentsLoading,
    errors,
    symptoms,
    selectedContract,
  } = useAppSelector(selectCreateJobOptions);
  const { assignToMe, category, customer, description, equipment, files, symptomCode1 } =
    useAppSelector(selectCreateJobForm);

  const intl = useIntl();
  const hasBarcodeScanner = !!scanditLicense();
  const isEquipmentMandatory = engineerSettings?.isEquipmentMandatory || false;
  const [currentPhoto, setCurrentPhoto] = useState<FileType | undefined>();
  const [equipmentACOpen, setEquipmentACOpen] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCloseConfirmation, setOpenCloseConfirmation] = useState(false);
  const [openScanner, setOpenScanner] = useState(false);
  const [scannerValue, setScannerValue] = useState<string>("");
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const [equipmentSearch, setEquipmentSearch] = useState<string>("");
  const debouncedEquipmentSearch = useDebounce<string>(equipmentSearch, 500);
  const debouncedCustomerSearch = useDebounce<string>(customerSearch, 500);
  const [parts, setParts] = useState<PartSelection[]>([]);

  const close = () => {
    // setCustomerSearch("");
    dispatch(handleClose());
  };

  const handleFileInput = async (e: any) => {
    const file = e.target.files[0];
    if (file) {
      resizeUploadFile(file).then((data) => {
        const output = typeof data === "string" ? data : "";
        const newFile: FileType = {
          id: nanoid(),
          name: file.name,
          data: output,
          description: "",
          size: output.length,
        };
        dispatch(setValue({ field: "files", value: [newFile, ...files] }));
      });
    }
    e.target.value = null;
  };

  const setCustomer = useCallback(
    async (id: string | null | undefined, name: string | null | undefined) => {
      if (id) {
        let customer = customers.find((x) => x.id === id);
        if (!customer) {
          await dispatch(getCustomer({ id: id, name: name }));
          customer = customers.find((x) => x.id === id);
        }
        if (customer) {
          dispatch(setValue({ field: "customer", value: customer }));
        } else {
          console.log("Something went wrong");
        }
      }
    },
    [customers, dispatch]
  );

  useEffect(() => {
    dispatch(setEquipmentMandatory({ isEquipmentMandatory }));
  }, [dispatch, isEquipmentMandatory]);

  useEffect(() => {
    if (open) {
      dispatch(getCreateJobOptions());
    }
  }, [dispatch, open, propsCustomer?.id, propsCustomer?.name]);

  useEffect(() => {
    if (open) {
      if (propsCustomer?.id) {
        setCustomer(propsCustomer?.id, propsCustomer?.name);
      }
    }
  }, [open, propsCustomer, setCustomer]);

  useEffect(() => {
    if (open) {
      dispatch(
        getEquipment({
          serialNumber: debouncedEquipmentSearch,
          customerId: customer?.id,
        })
      );
    }
  }, [debouncedEquipmentSearch, customer, open, dispatch]);

  useEffect(() => {
    if (open) {
      dispatch(getCustomers({ nameFilter: debouncedCustomerSearch }));
    }
  }, [debouncedCustomerSearch, dispatch, open]);

  useEffect(() => {
    if (open) {
      if (equipment?.id) {
        if (equipment.customerId) {
          setCustomer(equipment.customerId, equipment.customerName);
        }
        dispatch(getEquipmentContractStatus());
      }
    }
  }, [equipment, open, setCustomer, dispatch]);

  useEffect(() => {
    if (open) {
      dispatch(
        getAlertNotes({
          customerId: customer?.id,
          contractId: selectedContract?.contractId,
          equipmentId: equipment?.id,
        })
      );
    }
  }, [open, customer?.id, equipment?.id, dispatch, selectedContract?.contractId]);

  const [toggleBoxEnabled, setToggleBoxEnabled] = useState(false);
  const [toggleBoxText, setToggleBoxText] = useState("");

  const onSubmit = async () => {
    dispatch(setValue({ field: "quoteDescription", value: toggleBoxText }));
    dispatch(setValue({ field: "isQuoteRequested", value: toggleBoxEnabled }));
    dispatch(validateForm());
    if (!isValid) return;

    try {
      if (toggleBoxEnabled && parts.length > 0) {
        const salesOrderInput = {
          referenceWithRevision: Number(toggleBoxText) || 0,
          customerShipTo: {
            id: customer?.id!,
            name: customer?.name ?? "",
          },
          deliveryTaxCodeId: customer?.deliveryTaxCodeId ?? 0,
          totalPrice: parts.reduce((sum, part) => {
            const lineTotal = (part.price || 0) * (part.quantity || 1);
            const roundedLineTotal = Math.round(lineTotal * 100) / 100;
            return sum + roundedLineTotal;
          }, 0),
          parts: parts.map((part) => ({
            partId: Number(part.id),
            name: part.name,
            quantity: part.quantity || 1,
            price: part.price || 0,
            isNonStock: part.isNonStock || false,
            nominalCode: part.nominalCode || "",
            costCentreCode: part.costCentreCode || "",
            departmentCode: part.departmentCode || ""
          })),
          creditDays: customer?.creditDays ?? 0,
          projectReference: customer?.projectReference ?? "",
          countryCode: customer?.countryCode ?? "",
          currencyCode: customer?.currencyCode ?? "",
          storeId: customer?.suppliedStoreId ?? 0
        };

        const salesOrderResult = await dispatch(
          asyncMutations.createSalesOrder(salesOrderInput)
        ).then(unwrapResult);

        if (salesOrderResult.queued) {
          dispatch(addSnackbarMessage({ key: "CreateSalesOrder-queued" }));
        } else {
          dispatch(
            addSnackbarMessage({
              key: "CreateSalesOrder-success",
              argument: salesOrderResult.summaryId.toString(),
            })
          );
        }
      }

      const jobResult = await dispatch(createJob()).then(unwrapResult);
      close();
      if (jobResult.queued) {
        dispatch(addSnackbarMessage({ key: "CreateJob-stored" }));
      } else {
        dispatch(
          addSnackbarMessage({
            key: "CreateJob-success",
            argument: jobResult.jobId!.toString(),
          })
        );
        await dispatch(getJobs({ force: true })).then(unwrapResult);
        dispatch(getPartsCustomer());
      }
    } catch (e) {
      if (isAbortError(e)) return;
      dispatch(
        addSnackbarMessage({
          key: toggleBoxEnabled && parts.length > 0 ? "CreateSalesOrder-fail" : "CreateJob-fail",
        })
      );
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={close} data-testid="createJobDialog">
      <BackdropPrimaryMain open={createJobLoading || initLoading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={close}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="createJobCloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            <FormattedMessage id="menu.createNewJob" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer>
        {!initLoading && (
          <>
            <Grid container direction="column" spacing={2}>
              <Grid item container alignItems="start" justifyContent="space-between" wrap="nowrap">
                <Grid item width="100%">
                  <Autocomplete
                    className="e2e-create-job-equipment"
                    open={equipmentACOpen}
                    onOpen={() => setEquipmentACOpen(true)}
                    onClose={() => setEquipmentACOpen(false)}
                    value={equipment}
                    onChange={(_, value, reason) => {
                      if (reason === "clear") {
                        dispatch(setValue({ field: "equipment", value: null }));
                        setEquipmentSearch("");
                      } else {
                        if (value) {
                          dispatch(setValue({ field: "equipment", value }));
                        }
                      }
                    }}
                    getOptionLabel={(o) => `${o.serialNumber}`}
                    renderOption={(props, option) => {
                      return (
                        <li
                          {...props}
                          key={`${option.id}-${option.itemId}-${option.name}`}
                          style={{ display: "block" }}
                        >
                          <Typography variant="body1">{option.serialNumber}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {option.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {option.assetNumber}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {option.location}
                          </Typography>
                        </li>
                      );
                    }}
                    inputValue={equipmentSearch}
                    onInputChange={(_, value) => {
                      if (scannerValue && !value) {
                        setEquipmentSearch(scannerValue);
                      } else {
                        setScannerValue("");
                        setEquipmentSearch(value);
                      }
                    }}
                    isOptionEqualToValue={(option, value) =>
                      value && value.id ? option.id === value.id : true
                    }
                    filterOptions={(x) => x}
                    options={equipments}
                    loading={equipmentsLoading}
                    renderInput={(params) => (
                      <StyledTextField
                        name="equipment"
                        error={!!errors?.equipment}
                        {...params}
                        label={intl.formatMessage({
                          id: "job.equipment.serialNumber",
                        })}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <Fragment>
                              {equipmentsLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </Fragment>
                          ),
                        }}
                        required={isEquipmentMandatory}
                      />
                    )}
                  />
                  {!!equipment?.id && (
                    <StyledDetails>
                      {!!equipment.location && (
                        <Typography variant="body2" color="textSecondary">
                          {equipment.location}
                        </Typography>
                      )}
                      {!!equipment.name && (
                        <Typography variant="body2" color="textSecondary">
                          {equipment.name}
                        </Typography>
                      )}
                      {!!equipment.assetNumber && (
                        <Typography variant="body2" color="textSecondary">
                          {equipment.assetNumber}
                        </Typography>
                      )}
                      {!!equipment.address && (
                        <Typography variant="body2" color="textSecondary">
                          {addressToString(equipment.address)}
                        </Typography>
                      )}
                    </StyledDetails>
                  )}
                  <FormHelperText error>{errors?.equipment}</FormHelperText>
                </Grid>
                {hasBarcodeScanner && (
                  <Grid item>
                    <IconButton
                      onClick={() => setOpenScanner(true)}
                      size="large"
                      sx={{ pr: 0, ml: 1 }}
                    >
                      <CropFreeIcon fontSize="large" />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
              <Grid item>
                <Autocomplete
                  className="e2e-create-job-customer"
                  disableClearable={!!equipment?.id}
                  value={customer}
                  onChange={(_, value, reason) => {
                    if (reason === "clear") {
                      dispatch(setValue({ field: "customer", value: null }));
                    } else {
                      if (value) {
                        setEquipmentSearch("");
                        setCustomerSearch("");
                        dispatch(setValue({ field: "customer", value }));
                      }
                    }
                  }}
                  getOptionLabel={(o) => `${o.name}`}
                  renderOption={(props, option) => (
                    <li {...props} key={`${option.id}-${option.name}`} style={{ display: "block" }}>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {addressToPostalLocation(option.address)}
                      </Typography>
                    </li>
                  )}
                  inputValue={customerSearch}
                  onInputChange={(_, value) => {
                    setCustomerSearch(value);
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value && value.id ? option.id === value.id : true
                  }
                  filterOptions={(x) => x}
                  options={customers}
                  loading={customersLoading}
                  renderInput={(params) => (
                    <StyledTextField
                      name="customer"
                      error={!!errors?.customer}
                      {...params}
                      label={intl.formatMessage({
                        id: "general.customer",
                      })}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {customersLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                      required
                    />
                  )}
                />
                {!!customer?.id && customer.isOnStop && (
                  <div
                    style={{
                      display: "flex",
                      marginBottom: 5,
                      marginTop: 10,
                      alignItems: "center",
                    }}
                  >
                    <ErrorRoundedIcon color="error" sx={{ mr: 0.5 }} />
                    <FormattedMessage id="general.onStop" />
                  </div>
                )}
                <FormHelperText error>{errors?.customer}</FormHelperText>
              </Grid>
              {!!categories?.length && (
                <Grid item>
                  <Autocomplete
                    className="e2e-create-job-category"
                    options={categories}
                    getOptionLabel={(c: JobCategoryType) => `${c.text}`}
                    renderInput={(params) => (
                      <StyledTextField
                        name="categoryId"
                        error={!!errors?.category}
                        {...params}
                        label={intl.formatMessage({ id: "job.category" })}
                        required
                      />
                    )}
                    onChange={(_, value: JobCategoryType | null) => {
                      dispatch(
                        setValue({
                          field: "category",
                          value: value,
                        })
                      );
                      if (!value) dispatch(setValue({ field: "symptomCode1", value: null }));
                      if (symptomCode1 && symptomCode1 !== "") {
                        const symptom = symptoms.find((x) => x.code === symptomCode1);
                        if (symptom?.typeId && symptom.typeId !== value?.typeId) {
                          dispatch(
                            setValue({
                              field: "symptomCode1",
                              value: null,
                            })
                          );
                        }
                      }
                    }}
                    fullWidth
                  />
                  <FormHelperText error>{errors?.category}</FormHelperText>
                </Grid>
              )}
              {!!symptoms?.length && (
                <SymptomAutocomplete
                  symptoms={symptoms}
                  category={category}
                  errors={errors}
                  onChange={(field, value, shouldValidate) => {
                    dispatch(setValue({ field, value, shouldValidate }));
                  }}
                />
              )}
              <Grid item>
                <StyledTextField
                  className="e2e-create-job-description"
                  name="description"
                  fullWidth
                  required
                  label={intl.formatMessage({ id: "createJob.description" })}
                  multiline
                  rows={5}
                  value={description}
                  onChange={(e: any) => {
                    dispatch(
                      setValue({
                        field: "description",
                        value: e.target.value,
                      })
                    );
                  }}
                  error={!!errors?.description}
                />
                <FormHelperText error>{errors?.description}</FormHelperText>
              </Grid>
              <Grid item container justifyContent="space-between" alignItems="center" wrap="nowrap">
                <Grid item ml={0.5}>
                  <Typography>
                    <FormattedMessage id="createJob.assignToMe" />
                  </Typography>
                </Grid>
                <Grid item>
                  <AsolviSwitch
                    className="e2e-create-job-assign-to-me"
                    checked={assignToMe}
                    onChange={(_) =>
                      dispatch(setValue({ field: "assignToMe", value: !assignToMe }))
                    }
                  />
                </Grid>
              </Grid>
              <Grid item container justifyContent="space-between" alignItems="center" wrap="nowrap">
              <Grid item ml={0.5}>
                <Typography>
                  <FormattedMessage id="createJob.toggleSendEmail" />
                </Typography>
              </Grid>
              <Grid item>
                <AsolviSwitch
                  checked={toggleBoxEnabled}
                  onChange={() => setToggleBoxEnabled(!toggleBoxEnabled)}
                />
              </Grid>
            </Grid>
            {toggleBoxEnabled && (
              <Grid item>
                <StyledTextField
                  fullWidth
                  label={intl.formatMessage({ id: "createJob.quoteDescription" })}
                  value={toggleBoxText}
                  onChange={(e) => setToggleBoxText(e.target.value)}
                  error={!!errors?.quoteDescription}
                />
                <FormHelperText error>{errors?.quoteDescription}</FormHelperText>
              </Grid>
              )}
              {toggleBoxEnabled && (
                <PartsSelector open={open} parts={parts} setParts={setParts} />
              )}
              <Grid item>
                <input
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  id="create-job-attach-file"
                  type="file"
                  data-testid="CreateJobDialog-FileInput"
                  onChange={handleFileInput}
                  hidden
                />
                <label htmlFor="create-job-attach-file">
                  <SecondaryButton
                    startIcon={<UploadFileIcon />}
                    sx={{ justifyContent: "flex-start" }}
                    component="span"
                  >
                    {intl.formatMessage({ id: "createJob.attachFile" })}
                  </SecondaryButton>
                </label>
              </Grid>
              {files?.map((file, index) => (
                <FilePreview
                  key={file.id}
                  file={file}
                  onDelete={() => {
                    dispatch(
                      setValue({
                        field: "files",
                        value: files.filter((_, idx) => idx !== index),
                      })
                    );
                  }}
                  onEdit={() => {
                    setCurrentPhoto(file);
                    setOpenEditDialog(true);
                  }}
                />
              ))}
              <Grid item>
                <PrimaryButton
                  key={`create-job-submit-button`}
                  fullWidth
                  variant="contained"
                  disabled={!isValid || !isDirty}
                  className="e2e-create-job-submit"
                  type="submit"
                  data-testid="CreateJobDialog-SaveButton"
                  onClick={onSubmit}
                >
                  <FormattedMessage id="general.save" />
                </PrimaryButton>
              </Grid>
            </Grid>
            {hasBarcodeScanner && (
              <BarcodeScannerDialog
                open={openScanner}
                setOpen={(open: boolean) => setOpenScanner(open)}
                submitValue={(value: string) => {
                  setScannerValue(value);
                  setEquipmentSearch(value);
                  setOpenScanner(false);
                  setEquipmentACOpen(true);
                }}
              />
            )}
          </>
        )}
      </StyledContainer>
      <FullScreenDialog
        open={openEditDialog}
        setOpen={(open: boolean) => setOpenCloseConfirmation(!open)}
        child={
          <EditPhoto
            setOpenEditDialog={setOpenEditDialog}
            currentPhoto={currentPhoto}
            onSave={(result) => {
              const indexToUpdate = files.findIndex((x) => x.id === currentPhoto?.id);
              if (indexToUpdate > -1) {
                const filesTemp = [...files];
                filesTemp[indexToUpdate] = result;
                dispatch(setValue({ field: "files", value: filesTemp }));
              }
            }}
            openCloseConfirmation={openCloseConfirmation}
            setOpenCloseConfirmation={setOpenCloseConfirmation}
          />
        }
      />
      <CreateJobAlertNotesDialog alertNotes={alertNotes} />
    </Dialog>
  );
};